import { useNavigate } from "react-router-dom";
import {
    TableCell,
    TableRow,
    Box,
    Checkbox,
    IconButton,
    Tooltip,
    Typography,
    CircularProgress,
} from "@mui/material";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import RerunIcon from "../../assets/iconComponents/RerunIcon";
import TaskService from "../../services/TaskService";
import { useNotification } from "../../context/NotificationContext";
import { calculateRunTime } from "../../utils/calculateRunTime";
import { formatDate } from "../../utils/formatDate";

const UNSUPPORTED_PAGE_ERROR = "This type of page isn't supported yet.";
const UNSUPPORTED_NEGATIVE_SCENARIOS_ERROR =
    "Unsupported or unknown page type for negative scenario generation";
const UNSUPPORTED_PAGE_DETAILS =
    "Please use Test Journeys for this functionality.";
const isUnsupportedPageError = (error) =>
    [UNSUPPORTED_PAGE_ERROR, UNSUPPORTED_NEGATIVE_SCENARIOS_ERROR].some(
        (message) => String(error || "").includes(message)
    );

const TaskListItem = ({
    task,
    checkedTasks,
    tasksToggler,
    setTasksToggler,
    handleCheckboxChange,
    handleOpenDeleteDialog,
}) => {
    const navigate = useNavigate();
    const showNotification = useNotification();
    const isUnsupportedPageTask = isUnsupportedPageError(task?.error);

    const statusStyles = {
        pending: { backgroundColor: "#6D6E6F", color: "#fff" },
        in_progress: { backgroundColor: "#4574D2", color: "#fff" },
        completed: { backgroundColor: "#198754", color: "#fff" },
        failed: { backgroundColor: "#D32F2F", color: "#fff" },
        stopped: { backgroundColor: "#F8DF73", color: "#2D4356" },
    };

    const getTaskTypeLabel = (taskType) => {
        switch (taskType) {
            case "generateNegativeScenarios":
                return "Generate Negative Scenarios";
            default:
                return taskType;
        }
    };

    const handleRowClick = (task) => {
        console.log("task data:", task);
        const testRunId =
            task?.data?.testCaseRunId || task?.data?.testCaseRun?.id;
        if (testRunId) {
            navigate(`/test-run/${testRunId}`);
            return;
        }
        if (
            task.task_type === "Website Mapping" ||
            task.task_type === "Page Tree Scan"
        ) {
            navigate("/pages", { state: { view: "hierarchical" } });
        } else if (
            task.task_type === "Page Prototype Analyze" ||
            task.task_type === "pagePrototypeAnalyze"
        ) {
            navigate("/page-prototypes");
        } else if (
            task.task_type === "UI Scan" ||
            task.task_type === "Test Case"
        ) {
            // If a test case task is bound to a journey, redirect to Journey Test Cases
            
            const journeyId = task?.data?.testCase?.journey_id;
            if (task.task_type === "Test Case" && journeyId) {
                navigate(`/test-journey/${journeyId}`, {
                    state: { activeTab: "testCases" },
                });
            } else {
                navigate(`/pages/${task.page_id}`);
            }
        } else if (task.task_type === "Journey Test Case Generation") {
            const journeyId = task?.data?.journeyId;
            if (journeyId) {
                navigate(`/test-journey/${journeyId}`, {
                    state: { activeTab: "testCases" },
                });
            } else {
                navigate("/");
            }
        } else if (task.task_type === "Test Case Discovery") {
            navigate(`/pages/${task.page_id}`, { state: { activeTab: "3" } });
        } else if (task.task_type === "generateNegativeScenarios") {
            const journeyId = task?.data?.journey_id;
            if (journeyId) {
                navigate(`/test-journey/${journeyId}`, { state: { activeTab: "testCases" } });
            } else {
                navigate(`/pages/${task.page_id}`, { state: { activeTab: "3" } });
            }
        } else if (
            task.task_type === "Login Test" ||
            task.task_type === "Login Elements Processing" ||
            task.task_type === "reLogin"
        ) {
            navigate(`/project/${task.project_id}`, {
                state: { activeTab: "2" },
            });
        } else {
            navigate("/");
        }
    };

    const handleStopTask = async () => {
        try {
            await TaskService.stopTaskById(task.id);
            showNotification(
                "Task has been stopped successfully",
                "success",
                "",
                navigate
            );
            setTasksToggler(!tasksToggler);
        } catch (error) {
            console.log(error.message);
            showNotification(
                "Task could not be stopped",
                "error",
                "",
                navigate
            );
        }
    };

    const handleRerunTask = async () => {
        try {
            await TaskService.rerunTaskById(task.id);
            showNotification(
                "Task has been rerun successfully",
                "info",
                "",
                navigate
            );
            setTasksToggler(!tasksToggler);
        } catch (error) {
            console.log(error.message);
            showNotification("Task could not be rerun", "error", "", navigate);
        }
    };

    return (
        <TableRow
            key={task?.id}
            className="tasksTableBodyRow"
            hover
            onClick={() => handleRowClick(task)}
        >
            <TableCell
                className="tasksTableBodyCheckboxCell"
                onClick={(e) => e.stopPropagation()}
            >
                <Checkbox
                    className="tasksTableBodyCheckbox"
                    checked={checkedTasks.includes(task?.id)}
                    onChange={() => handleCheckboxChange(task?.id)}
                />
            </TableCell>
            <TableCell className="tasksTableBodyCell">
                {getTaskTypeLabel(task.task_type)}
            </TableCell>
            <TableCell className="tasksTableBodyCell">
                {task.page_name ? task.page_name : "N/A"}
            </TableCell>
            <TableCell className="tasksTableBodyCell">
                {task.created_by || "-"}
            </TableCell>
            <TableCell className="tasksTableBodyCell">
                {task.status !== "pending"
                    ? formatDate(task.started_at)
                    : "N/A"}
            </TableCell>
            <TableCell className="tasksTableBodyCell">
                {task.status === "pending" ? null : task.status ===
                  "in_progress" ? (
                    <CircularProgress size={18} />
                ) : (
                    calculateRunTime(task.started_at, task.updated_at)
                )}
            </TableCell>
            <TableCell className="tasksTableBodyCell">
                <Box className="tasksTableBodyStatusContainer">
                    {(() => {
                        const displayStatus = task.status;
                        const style = isUnsupportedPageTask
                            ? { backgroundColor: "#FF9800", color: "#fff" }
                            : statusStyles[displayStatus];
                        const label = isUnsupportedPageTask
                            ? "Unsupported"
                            : `${
                                  displayStatus === "in_progress"
                                      ? "in progress"
                                      : displayStatus
                              }${
                                  displayStatus === "pending" ||
                                  displayStatus === "in_progress"
                                      ? "..."
                                      : ""
                              }`;

                        return (
                            <Box className="tasksTableBodyStatus" sx={style}>
                                {label}
                            </Box>
                        );
                    })()}
                </Box>
            </TableCell>
            <TableCell className="tasksTableBodyCell">
                {task?.Page?.screen_size && task.task_type === "UI Scan" ? (
                    <span style={{ textTransform: "capitalize" }}>
                        {task.Page.screen_size}
                    </span>
                ) : isUnsupportedPageTask ? (
                    UNSUPPORTED_PAGE_DETAILS
                ) : (
                    ""
                )}
            </TableCell>
            <TableCell
                className="tasksTableBodyCell"
                sx={{ wordBreak: "break-word" }}
            >
                {task?.error && task.error.length > 50 ? (
                    <Tooltip
                        title={
                            <Typography
                                variant="body2"
                                sx={{
                                    whiteSpace: "normal",
                                    textAlign: "left",
                                    color: "#fff",
                                    fontSize: "0.6875rem",
                                }}
                            >
                                {task?.error}
                            </Typography>
                        }
                        PopperProps={{
                            sx: {
                                "& .MuiTooltip-tooltip": {
                                    backgroundColor: "primary.main",
                                    maxWidth: "260px",
                                },
                            },
                        }}
                    >
                        <span>{`${task.error.slice(0, 50)}...`}</span>
                    </Tooltip>
                ) : (
                    task.error
                )}
            </TableCell>
            <TableCell className="tasksTableBodyCell tasksActions">
                {(task.status === "in_progress" || task.status === "pending") &&
                task.task_type !== "Login Test" ? (
                    <Tooltip
                        title="Stop"
                        PopperProps={{
                            sx: {
                                "& .MuiTooltip-tooltip": {
                                    backgroundColor: "primary.main",
                                },
                            },
                        }}
                    >
                        <IconButton
                            className="tasksActionsIconContainer"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleStopTask();
                            }}
                        >
                            <StopCircleOutlinedIcon color="primary" />
                        </IconButton>
                    </Tooltip>
                ) : (task.status === "in_progress" ||
                      task.status === "pending") &&
                  task.task_type === "Login Test" ? (
                    <IconButton
                        className="tasksActionsIconContainer"
                        disabled
                        onClick={(e) => e.stopPropagation()}
                    >
                        <StopCircleOutlinedIcon />
                    </IconButton>
                ) : null}
                {task.status !== "in_progress" &&
                task.status !== "pending" &&
                task.task_type !== "Login Test" ? (
                    <Tooltip
                        title="Re-run"
                        PopperProps={{
                            sx: {
                                "& .MuiTooltip-tooltip": {
                                    backgroundColor: "primary.main",
                                },
                            },
                        }}
                    >
                        <IconButton
                            className="tasksActionsIconContainer"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRerunTask();
                            }}
                        >
                            <RerunIcon color="#2D4356" />
                        </IconButton>
                    </Tooltip>
                ) : task.status !== "in_progress" &&
                  task.status !== "pending" &&
                  task.task_type === "Login Test" ? (
                    <IconButton
                        className="tasksActionsIconContainer"
                        disabled
                        onClick={(e) => e.stopPropagation()}
                    >
                        <RerunIcon color="rgba(0, 0, 0, 0.26)" />
                    </IconButton>
                ) : null}
                <Tooltip
                    title="Remove"
                    PopperProps={{
                        sx: {
                            "& .MuiTooltip-tooltip": {
                                backgroundColor: "primary.main",
                            },
                        },
                    }}
                >
                    <IconButton
                        className="tasksActionsIconContainer deleteAction"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDeleteDialog(task?.id);
                        }}
                    >
                        <DeleteOutlineOutlinedIcon color="error" />
                    </IconButton>
                </Tooltip>
            </TableCell>
        </TableRow>
    );
};

export default TaskListItem;
